import { useState, useCallback, useRef, useMemo, useEffect, type ReactNode } from 'react'
// FlashList v2 chat. v2 dropped `inverted`; the canonical
// replacement is `maintainVisibleContentPosition.startRenderingFromBottom`
// + chronological data + onStartReached for older-message pagination.
// See https://shopify.github.io/flash-list/docs/v2-migration/.
import {
  View,
  Text,
  TextInput as RNTextInput,
  Platform,
  Keyboard,
  KeyboardAvoidingView,
  ActivityIndicator,
  Linking,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type TextInputKeyPressEvent,
} from 'react-native'
import { FlashList, type FlashListRef } from '@shopify/flash-list'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import {
  Camera,
  ChevronDown,
  Image as ImageIcon,
  Lock,
  MapPin,
  MessageCircle,
  MoreVertical,
  Paperclip,
  Phone,
  Search,
  Send,
  X,
} from '@/components/ui/icons'
import { useRouter } from 'expo-router'
import { impact, ImpactStyle, selection } from '@/lib/utils/haptics'
import * as ImagePicker from 'expo-image-picker'
import * as Crypto from 'expo-crypto'
import * as Location from 'expo-location'
import { AppModal } from '@/components/ui/AppModal'
import { compressAndStore } from '@/lib/utils/image-compress'
import { toast } from '@/lib/toast'
import { filterMessage } from '@/lib/utils/message-filter'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { BackButton } from '@/components/ui/BackButton'
import { Avatar } from '@/components/ui/Avatar'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import { BlockUserButton } from '@/components/ui/BlockUserButton'
import { MessageBubble } from '@/components/chat/MessageBubble'
import { ConversationTripContext } from '@/components/chat/ConversationTripContext'
import { ReplyContext } from '@/components/chat/ReplyContext'
import { PhotoLightbox, usePhotoLightbox } from '@/components/ui/PhotoLightbox'
import { useConversationMessageSearch, useMessages, useSendMessage } from '@/lib/hooks/use-messages'
import { useConversations } from '@/lib/hooks/use-conversations'
import { useBooking } from '@/lib/hooks/use-booking'
import { useCanMessageHost } from '@/lib/hooks/use-can-message-host'
import { useTypingIndicator } from '@/lib/hooks/use-typing-indicator'
import { useReadReceipts } from '@/lib/hooks/use-read-receipts'
import { conversationCallHref, messagingService } from '@/lib/services/messaging.service'
import { useAuth } from '@/lib/hooks/use-auth'
import { useMessagingStore } from '@/lib/stores/messaging.store'
import { useUIStore } from '@/lib/stores/ui.store'
import { INTERACTION, SHADOWS, useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import {
  bookingDateKey,
  formatBookingDate,
  formatBookingDateTime,
  todayInBookingTimeZoneISO,
} from '@/lib/utils/dates'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { RETENTION_EVENTS } from '@/lib/retention/events'
import type {
  MessageItem,
  MessageReplyPreview,
  MessageSearchResult,
} from '@/lib/types/messaging.types'

const CHAT_HEADER_GEOMETRY = {
  horizontalPadding: SPACING.lg,
  verticalPadding: SPACING.sm,
  gap: SPACING.sm,
  controlSize: INTERACTION.minTouchTarget,
  avatarSize: 32,
  avatarFramePadding: 2,
  avatarFrameBorderWidth: 1,
  titleFontSize: 16,
  titleLineHeight: 21,
  subtitleFontSize: 12,
  subtitleLineHeight: 16,
} as const

const CHAT_COMPOSER_GEOMETRY = {
  horizontalPadding: SPACING.md,
  topPadding: SPACING.sm,
  bottomPaddingResting: SPACING.sm,
  bottomPaddingKeyboard: SPACING.xs,
  outerGap: SPACING.sm,
  innerGap: SPACING.xs,
  inputHeight: 48,
  maxInputHeight: 120,
  lineHeight: 21,
  textPaddingVertical: 12,
  textFontSize: 15,
  // 44, not 40: the attach button is a real control, and the composer row
  // is already 48 tall so it costs no layout.
  iconButtonSize: 44,
  iconSize: 20,
  sendButtonSize: 48,
  sendIconSize: 18,
  webCharsPerLine: 32,
} as const

const COMPOSER_MIN_HEIGHT = CHAT_COMPOSER_GEOMETRY.inputHeight
const COMPOSER_MAX_HEIGHT = CHAT_COMPOSER_GEOMETRY.maxInputHeight
const COMPOSER_LINE_HEIGHT = CHAT_COMPOSER_GEOMETRY.lineHeight
const COMPOSER_VERTICAL_PADDING = CHAT_COMPOSER_GEOMETRY.textPaddingVertical * 2
const WEB_COMPOSER_CHARS_PER_LINE = CHAT_COMPOSER_GEOMETRY.webCharsPerLine

/**
 * The answers a rental pair actually gives each other, as one-tap chips.
 *
 * This is the Uber surface: the four things that used to be typed — or worse,
 * moved to WhatsApp — are one tap inside the thread. Every string is a plain
 * status, deliberately containing no contact data, so the anti-disintermediation
 * filter has nothing to redact and the sender never has to think about it.
 */
const QUICK_REPLIES = [
  { key: 'on_my_way', labelKey: 'messages.quick_reply_on_my_way' },
  { key: 'arrived', labelKey: 'messages.quick_reply_arrived' },
  { key: 'on_time', labelKey: 'messages.quick_reply_on_time' },
  { key: 'thanks', labelKey: 'messages.quick_reply_thanks' },
] as const

/** One row of the attach sheet. 56dp, label-first, no icon-only ambiguity. */
function AttachOption({
  icon,
  label,
  testID,
  onPress,
}: {
  icon: ReactNode
  label: string
  testID: string
  onPress: () => void
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      testID={testID}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        minHeight: 56,
        borderRadius: RADIUS.lg,
        paddingHorizontal: SPACING.md,
        backgroundColor: pressed ? colors.surfaceSubtle : 'transparent',
      })}
    >
      {icon}
      <Text style={{ fontFamily: FONTS.body, fontSize: 16, color: colors.textPrimary }}>
        {label}
      </Text>
    </Pressable>
  )
}
const MESSAGE_CLUSTER_WINDOW_MS = 5 * 60 * 1000
const JUMP_TO_LATEST_THRESHOLD = CHAT_COMPOSER_GEOMETRY.inputHeight * 5 + SPACING.md

type MessageSendSource = 'button' | 'keyboard_enter' | 'submit_editing'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

// Calendar days are pinned to America/El_Salvador (lib/utils/dates.ts) so the
// separators, the clustering window and the conversation-list timestamps all
// agree on where one day ends — the device timezone does not get a vote.
function dayKey(iso: string): string {
  return bookingDateKey(iso) ?? new Date(iso).toDateString()
}

function isSameDay(a: string, b: string): boolean {
  return dayKey(a) === dayKey(b)
}

function formatDayLabel(iso: string, t: (key: string) => string, locale: string): string {
  const key = bookingDateKey(iso)
  if (!key) return ''

  if (key === todayInBookingTimeZoneISO()) return t('messages.today')
  if (key === bookingDateKey(new Date(Date.now() - ONE_DAY_MS))) return t('messages.yesterday')

  return formatBookingDate(iso, locale, { month: 'short', day: 'numeric', year: undefined })
}

function isSameMessageCluster(a: MessageItem | undefined, b: MessageItem | undefined): boolean {
  if (!a || !b) return false
  if (a.messageType === 'system' || b.messageType === 'system') return false
  if (a.senderId !== b.senderId) return false
  if (!isSameDay(a.createdAt, b.createdAt)) return false
  return (
    Math.abs(new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) <=
    MESSAGE_CLUSTER_WINDOW_MS
  )
}

function estimateWebComposerHeight(value: string): number {
  const rows = value.split('\n').reduce((count, line) => {
    return count + Math.max(1, Math.ceil(line.length / WEB_COMPOSER_CHARS_PER_LINE))
  }, 0)
  const measured = rows * COMPOSER_LINE_HEIGHT + COMPOSER_VERTICAL_PADDING
  return Math.min(COMPOSER_MAX_HEIGHT, Math.max(COMPOSER_MIN_HEIGHT, measured))
}

function messageLengthBucket(value: string): string {
  const length = value.trim().length
  if (length <= 40) return 'short'
  if (length <= 180) return 'medium'
  if (length <= 700) return 'long'
  return 'very_long'
}

function toReplyPreview(message: MessageItem): MessageReplyPreview {
  return {
    id: message.id,
    senderId: message.senderId,
    content: message.content,
    messageType: message.messageType,
    isDeleted: message.isDeleted,
  }
}

function ConversationSearchResultRow({
  result,
  isMine,
  otherUserName,
  locale,
}: {
  result: MessageSearchResult
  isMine: boolean
  otherUserName: string
  locale: string
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID="conversation.search-result"
      accessibilityRole="summary"
      accessibilityLabel={`${t('messages.search_result_a11y')}: ${result.content ?? ''}`}
      style={{
        marginHorizontal: SPACING.md,
        marginBottom: SPACING.sm,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.11)' : 'rgba(26,21,37,0.08)',
        backgroundColor: colors.surface,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.sm,
        }}
      >
        <Text
          style={{ flex: 1, fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textPrimary }}
        >
          {isMine ? t('messages.you') : otherUserName}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
          {formatBookingDateTime(result.createdAt, locale, {
            day: 'numeric',
            month: 'short',
            year: undefined,
          })}
        </Text>
      </View>
      <Text
        numberOfLines={3}
        style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 15, color: colors.textPrimary }}
      >
        {result.content ?? t('messages.reply_message')}
      </Text>
    </View>
  )
}

export default function ConversationScreen() {
  const { conversationId } = useLocalSearchParams<{ conversationId: string }>()
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const { user } = useAuth()
  const userId = user?.id ?? ''
  const router = useRouter()

  const { messages, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useMessages(conversationId)
  const { sendMessage, retryMessage, isPending: isSending } = useSendMessage(conversationId ?? '')
  // Chat photos used to be dead images: the app already owns a zoomable,
  // swipeable viewer (`PhotoLightbox`) used by listings and checkout, and the
  // thread simply never wired it. Same viewer, same gestures, no new surface.
  const lightbox = usePhotoLightbox()
  const { conversations, isLoading: isLoadingConversations } = useConversations()
  const conv = useMemo(
    () => conversations.find((c) => c.id === conversationId),
    [conversationId, conversations],
  )
  // The conversation already owns the rental ID; reuse the canonical booking
  // query instead of adding a second chat-specific rental fetch.
  const tripQuery = useBooking(conv?.rentalId ?? undefined)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const searchInputRef = useRef<RNTextInput>(null)
  const [replyingTo, setReplyingTo] = useState<MessageItem | null>(null)

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchText(searchText.trim()), 220)
    return () => clearTimeout(timeout)
  }, [searchText])

  useEffect(() => {
    // A different route is a different conversation. Carrying a draft reply
    // or a search term into it is both confusing and a privacy footgun.
    setIsSearchOpen(false)
    setSearchText('')
    setDebouncedSearchText('')
    setReplyingTo(null)
  }, [conversationId])

  const messageSearch = useConversationMessageSearch(conversationId, debouncedSearchText)

  // Pass our display name so the counterparty's UI can render
  // "<our name> is typing" without a profile lookup on their side.
  const localDisplayName = (user?.user_metadata?.display_name as string | undefined) ?? null
  const { isOtherTyping, typingUserName, setTyping } = useTypingIndicator(conversationId, userId, {
    displayName: localDisplayName,
  })
  const { draftMessages, setDraft, clearDraft } = useMessagingStore()
  const [text, setText] = useState(draftMessages[conversationId ?? ''] ?? '')
  const insets = useScreenInsets()
  /*
   * ¿Se puede escribir en esta conversación?
   *
   * El candado real vive en un trigger de Postgres y no se mueve: no se habla
   * con nadie hasta que la reserva está pagada. Esto es para que la PANTALLA lo
   * sepa antes, y muestre el estado en vez de dejar escribir un mensaje entero
   * para después tirarlo.
   *
   * `resolved` de por medio: mientras no haya respuesta se muestra el
   * compositor normal y decide el servidor. Bloquear a alguien que SÍ puede
   * escribir, porque se cayó una consulta, es la peor de las dos fallas.
   */
  const messagingGate = useCanMessageHost(conv?.otherUserId)
  const messagingLocked = messagingGate.resolved && !messagingGate.allowed
  const [composerHeight, setComposerHeight] = useState<number>(COMPOSER_MIN_HEIGHT)
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  /** Altura real del teclado. En Android es lo único que separa al compositor de quedar tapado. */
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const inputRef = useRef<RNTextInput>(null)
  const listRef = useRef<FlashListRef<MessageItem>>(null)
  const isInputFocusedRef = useRef(false)
  /*
   * En reposo el compositor tiene que despegarse de la barra de gestos.
   *
   * Esta pantalla usa `ScreenContainer` en `full-bleed`, que a propósito NO
   * aplica el inset inferior —lo quiere así el hilo de mensajes, que debe
   * llegar hasta el borde— pero el compositor sí lo necesita, o la caja de
   * texto queda pegada al borde inferior y debajo de la barra de gestos de
   * Android. Es la diferencia entre "una app" y algo que se siente terminado.
   *
   * Con el teclado ARRIBA el inset se suelta: el teclado ya ocupa esa franja, y
   * sumarlo deja el compositor flotando sobre una banda vacía.
   */
  const composerBottomPadding = isKeyboardVisible
    ? CHAT_COMPOSER_GEOMETRY.bottomPaddingKeyboard +
      // Solo Android: iOS ya lo resuelve con `behavior='padding'`, y sumarlo
      // ahí levantaría el compositor una pantalla de más.
      (Platform.OS === 'android' ? keyboardHeight : 0)
    : Math.max(insets.bottom, CHAT_COMPOSER_GEOMETRY.bottomPaddingResting)
  const [showJumpToLatest, setShowJumpToLatest] = useState(false)

  useEffect(() => {
    if (Platform.OS === 'web') return

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      setIsKeyboardVisible(true)
      // `endCoordinates.height` es lo que el sistema dice que ocupa. En iOS no
      // se usa —`behavior='padding'` ya lo resuelve— pero se guarda igual para
      // que las dos plataformas lean el mismo estado.
      setKeyboardHeight(event?.endCoordinates?.height ?? 0)
    })
    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setIsKeyboardVisible(false)
      setKeyboardHeight(0)
    })

    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [])

  // With FlashList v2 the data is chronological (oldest first)
  // because `inverted` is gone and `startRenderingFromBottom` handles
  // the initial scroll position. useMessages still returns DESC (newest
  // first); reverse here once per page-update via useMemo so that all
  // downstream renderItem callbacks get a stable reference. The reverse
  // is O(n) — even at the upper end of practical chat sizes (~500 msgs)
  // this is well under 100µs.
  const messagesAsc = useMemo(() => [...messages].reverse(), [messages])
  const fallbackCounterparty = useMemo(
    () => messagesAsc.find((message) => message.senderId !== userId) ?? null,
    [messagesAsc, userId],
  )

  // The counterpart is the only participant whose read cursor this screen
  // renders. Same resolution order as the header, from one value.
  const otherUserId = conv?.otherUserId ?? fallbackCounterparty?.senderId ?? null
  const theirLastReadAt = useReadReceipts(conversationId, otherUserId)
  const headerTitle =
    conv?.otherUserDisplayName ??
    fallbackCounterparty?.senderDisplayName ??
    t('messages.new_conversation')
  const headerAvatarUrl = conv?.otherUserAvatarUrl ?? null
  const headerTargetId = otherUserId

  /**
   * Every image in the thread, oldest → newest, for the lightbox.
   *
   * The array is memoized for the memo dependency graph, but the open handler
   * reads it through a ref: `renderItem` must stay identity-stable or FlashList
   * re-renders its whole recycle pool on every new message. The ref is the one
   * seam that lets the data be fresh while the callback stays frozen.
   */
  const lightboxImages = useMemo(
    () =>
      messagesAsc
        .filter((message) => message.messageType === 'image' && message.mediaUrl)
        .map((message) => ({
          id: message.id,
          uri: message.mediaUrl as string,
          blurhash: message.mediaBlurhash ?? null,
        })),
    [messagesAsc],
  )
  const lightboxImagesRef = useRef(lightboxImages)
  useEffect(() => {
    lightboxImagesRef.current = lightboxImages
  }, [lightboxImages])

  const { open: openLightbox } = lightbox
  const handleOpenImage = useCallback(
    (message: MessageItem) => {
      const images = lightboxImagesRef.current
      const index = images.findIndex((image) => image.id === message.id)
      openLightbox(images, index < 0 ? 0 : index)
    },
    [openLightbox],
  )

  // FlashList v2 ref API uses scrollToEnd to jump to the latest message
  // (visual bottom) since the data is now in chronological order.
  const scrollToBottom = useCallback(() => {
    listRef.current?.scrollToEnd({ animated: true })
  }, [])
  const settleToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      listRef.current?.scrollToEnd({ animated: false })
    })
  }, [])

  const replyAuthorLabel = useCallback(
    (reply: MessageReplyPreview | null) =>
      reply?.senderId === userId ? t('messages.you') : headerTitle,
    [headerTitle, t, userId],
  )

  const handleReplyMessage = useCallback(
    (message: MessageItem) => {
      if (
        message.id.startsWith('optimistic-') ||
        message.sendFailed ||
        message.messageType === 'system'
      )
        return
      if (Platform.OS !== 'web') selection()
      setReplyingTo(message)
      settleToBottom()
      // This focus is an explicit reply action, not a side effect of sending
      // or opening a conversation; it makes the long-press affordance useful
      // without summoning the keyboard at unrelated moments.
      requestAnimationFrame(() => inputRef.current?.focus())
    },
    [settleToBottom],
  )

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true)
    requestAnimationFrame(() => searchInputRef.current?.focus())
  }, [])

  const handleCloseSearch = useCallback(() => {
    Keyboard.dismiss()
    setIsSearchOpen(false)
    setSearchText('')
    setDebouncedSearchText('')
  }, [])

  const handleListScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
    if (!contentSize.height || !layoutMeasurement.height) return

    const distanceFromBottom = contentSize.height - layoutMeasurement.height - contentOffset.y
    setShowJumpToLatest(distanceFromBottom > JUMP_TO_LATEST_THRESHOLD)
  }, [])
  const handleTrackedListScroll = useProductTelemetryScroll(
    'conversation.message-list',
    handleListScroll,
  )

  // Synchronous double-submit guard — TanStack `isSending` flips on the NEXT
  // render tick, so a rapid second tap can slip through. Same pattern as
  // `app/booking/checkout.tsx`.
  const isSendingRef = useRef(false)

  const handleSend = useCallback(
    async (source: MessageSendSource) => {
      const trimmed = text.trim()
      if (!trimmed || isSending || isSendingRef.current) return
      const replyTo = replyingTo ? toReplyPreview(replyingTo) : null

      // Check for filtered content and warn the user
      const filterResult = filterMessage(trimmed)
      productTelemetryService.recordEvent('chat', 'chat.message_send_requested', {
        elementId: source === 'button' ? 'conversation.send-button' : 'conversation.message-input',
        elementType: source === 'button' ? 'button' : 'text_input',
        metadata: {
          source,
          lengthBucket: messageLengthBucket(trimmed),
          wasFiltered: filterResult.wasFiltered,
        },
      })
      if (filterResult.wasFiltered) {
        productTelemetryService.recordEvent('chat', RETENTION_EVENTS.contactNudgeShown, {
          elementId: 'conversation.message-input',
          metadata: { detectedTypes: filterResult.detectedTypes },
        })
        toast.info(t('messages.content_filtered'))
      }

      isSendingRef.current = true
      setText('')
      setComposerHeight(COMPOSER_MIN_HEIGHT)
      setReplyingTo(null)
      clearDraft(conversationId ?? '')
      setTyping(false)
      impact(ImpactStyle.Light)

      // Let FlashList stay pinned without running a competing scroll animation
      // while Android is resizing the window around the keyboard.
      settleToBottom()

      try {
        await sendMessage({
          content: trimmed,
          replyToId: replyTo?.id ?? null,
          replyTo,
        })
        productTelemetryService.recordEvent('chat', 'chat.message_send_succeeded', {
          elementId:
            source === 'button' ? 'conversation.send-button' : 'conversation.message-input',
          elementType: source === 'button' ? 'button' : 'text_input',
          metadata: {
            source,
            messageType: 'text',
            isReply: Boolean(replyTo),
            lengthBucket: messageLengthBucket(trimmed),
            wasFiltered: filterResult.wasFiltered,
          },
        })
      } catch (err) {
        productTelemetryService.recordEvent('chat', 'chat.message_send_failed', {
          elementId:
            source === 'button' ? 'conversation.send-button' : 'conversation.message-input',
          elementType: source === 'button' ? 'button' : 'text_input',
          metadata: {
            source,
            messageType: 'text',
            lengthBucket: messageLengthBucket(trimmed),
          },
        })
        // The failed bubble stays in the thread with its own retry, so the text
        // is deliberately NOT handed back to the composer: that would render
        // the same message twice and make "enviar" ambiguous. The toast stays
        // as the signal for a user who scrolled away from the bottom.
        if (__DEV__) console.error('[Chat] send failed:', err)
        toast.error(errorMessage(err, t('messages.send_failed')))
        captureError(err, { screen: 'messages', action: 'send' }, { conversationId })
      } finally {
        isSendingRef.current = false
      }
    },
    [
      text,
      isSending,
      replyingTo,
      conversationId,
      sendMessage,
      clearDraft,
      settleToBottom,
      setTyping,
      t,
    ],
  )

  /**
   * One-tap canned reply. Same send path and the same failure contract as a
   * typed message (the failed bubble keeps the retry), so a chip can never
   * behave differently from the composer it replaces.
   */
  const handleQuickReply = useCallback(
    async (key: string) => {
      if (isSendingRef.current) return
      const replyTo = replyingTo ? toReplyPreview(replyingTo) : null
      isSendingRef.current = true
      if (Platform.OS !== 'web') impact(ImpactStyle.Light)
      settleToBottom()
      try {
        setReplyingTo(null)
        await sendMessage({
          content: t(`messages.quick_reply_${key}`),
          replyToId: replyTo?.id ?? null,
          replyTo,
        })
      } catch (err) {
        toast.error(errorMessage(err, t('messages.send_failed')))
        captureError(err, { screen: 'messages', action: 'quick_reply' }, { conversationId })
      } finally {
        isSendingRef.current = false
      }
    },
    [conversationId, replyingTo, sendMessage, settleToBottom, t],
  )

  const handleTextChange = useCallback(
    (v: string) => {
      setText(v)
      if (Platform.OS === 'web') {
        setComposerHeight(estimateWebComposerHeight(v))
      }
      if (conversationId) setDraft(conversationId, v)
      setTyping(!!v.trim())
    },
    [conversationId, setDraft, setTyping],
  )

  const handleComposerKeyPress = useCallback(
    (event: TextInputKeyPressEvent) => {
      if (Platform.OS !== 'web') return

      const webEvent = event as TextInputKeyPressEvent & {
        key?: string
        shiftKey?: boolean
        isComposing?: boolean
        preventDefault?: () => void
      }
      const key = webEvent.key ?? webEvent.nativeEvent.key

      if (key !== 'Enter' || webEvent.shiftKey || webEvent.isComposing) return

      webEvent.preventDefault?.()
      void handleSend('keyboard_enter')
    },
    [handleSend],
  )

  const handleComposerSizeChange = useCallback(
    (height: number) => {
      if (Platform.OS === 'web') return

      const nextHeight = Math.min(
        COMPOSER_MAX_HEIGHT,
        Math.max(COMPOSER_MIN_HEIGHT, Math.ceil(height + SPACING.xs)),
      )
      setComposerHeight((currentHeight) => {
        if (Math.abs(currentHeight - nextHeight) < 2) return currentHeight
        return nextHeight
      })
      if (isInputFocusedRef.current) {
        settleToBottom()
      }
    },
    [settleToBottom],
  )

  const [isUploading, setIsUploading] = useState(false)
  // Synchronous guard — `setIsUploading` only flips after the picker resolves,
  // so a rapid second tap could launch the picker twice.
  const isPickingRef = useRef(false)
  const [isAttachOpen, setIsAttachOpen] = useState(false)
  const [callSheet, setCallSheet] = useState<null | 'loading' | 'ready' | 'unavailable'>(null)
  const callPhoneRef = useRef<string | null>(null)

  /**
   * One upload path for both sources. Camera and gallery differ only in where
   * the bytes come from; everything after (compress budget, private bucket,
   * optimistic bubble, telemetry) is identical by construction.
   */
  const sendImageUri = useCallback(
    async (uri: string, source: 'camera' | 'gallery') => {
      if (!conversationId) return
      const replyTo = replyingTo ? toReplyPreview(replyingTo) : null
      setIsUploading(true)
      if (Platform.OS !== 'web') {
        impact(ImpactStyle.Light)
      }
      try {
        const uploadPath = `${conversationId}/${Crypto.randomUUID()}.jpg`
        await compressAndStore(uri, 'chat-media', uploadPath, 'chat')
        // `chat-media` is private: store the storage path and let readers sign
        // it. A public URL would outlive the message and the conversation.
        await sendMessage({
          messageType: 'image',
          mediaUrl: uploadPath,
          replyToId: replyTo?.id ?? null,
          replyTo,
        })
        setReplyingTo(null)
        productTelemetryService.recordEvent('chat', 'chat.message_send_succeeded', {
          elementId: 'conversation.attach-image',
          elementType: 'button',
          metadata: { messageType: 'image', source },
        })
        settleToBottom()
      } catch (err) {
        productTelemetryService.recordEvent('chat', 'chat.message_send_failed', {
          elementId: 'conversation.attach-image',
          elementType: 'button',
          metadata: { messageType: 'image', source },
        })
        if (__DEV__) console.error('[Chat] Image upload failed:', err)
        toast.error(errorMessage(err, t('messages.image_send_failed')))
        captureError(err, { screen: 'messages', action: 'attach_image' }, { conversationId })
      } finally {
        setIsUploading(false)
      }
    },
    [conversationId, replyingTo, sendMessage, settleToBottom, t],
  )

  const handleOpenAttach = useCallback(() => {
    if (isUploading) return
    if (Platform.OS !== 'web') selection()
    setIsAttachOpen(true)
  }, [isUploading])

  const handlePickImage = useCallback(async () => {
    if (!conversationId) return
    if (isPickingRef.current || isUploading) return
    isPickingRef.current = true
    setIsAttachOpen(false)
    productTelemetryService.recordEvent('chat', 'chat.image_send_requested', {
      elementId: 'conversation.attach-image',
      elementType: 'button',
      metadata: { mediaType: 'image', source: 'gallery' },
    })
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.8,
        allowsEditing: false,
      })

      if (result.canceled || !result.assets[0]) {
        productTelemetryService.recordEvent('chat', 'chat.image_send_cancelled', {
          elementId: 'conversation.attach-image',
          elementType: 'button',
          metadata: { mediaType: 'image', source: 'gallery' },
        })
        return
      }

      await sendImageUri(result.assets[0].uri, 'gallery')
    } finally {
      isPickingRef.current = false
    }
  }, [conversationId, isUploading, sendImageUri])

  /**
   * Camera capture. `requestCameraPermissionsAsync` is asked immediately before
   * the intent, not at mount: a permission prompt on a chat that has not asked
   * to use the camera is exactly the friction this feature exists to remove.
   */
  const handleTakePhoto = useCallback(async () => {
    if (!conversationId) return
    if (isPickingRef.current || isUploading) return
    isPickingRef.current = true
    setIsAttachOpen(false)
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync()
      if (!permission.granted) {
        toast.error(t('messages.camera_permission_denied'))
        return
      }
      productTelemetryService.recordEvent('chat', 'chat.image_send_requested', {
        elementId: 'conversation.attach-camera',
        elementType: 'button',
        metadata: { mediaType: 'image', source: 'camera' },
      })
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        quality: 0.8,
        allowsEditing: false,
      })
      if (result.canceled || !result.assets[0]) return
      await sendImageUri(result.assets[0].uri, 'camera')
    } catch (err) {
      if (__DEV__) console.error('[Chat] camera failed:', err)
      captureError(err, { screen: 'messages', action: 'attach_camera' }, { conversationId })
    } finally {
      isPickingRef.current = false
    }
  }, [conversationId, isUploading, sendImageUri, t])

  /**
   * Location, the message type that removes the other half of the WhatsApp
   * pull. Uber put a pin in the chat and the "mándame tu ubicación" text
   * disappeared; this is that, on the same table the rest of the thread uses
   * (`messages.location_lat/lng`).
   */
  const handleShareLocation = useCallback(async () => {
    if (!conversationId) return
    const replyTo = replyingTo ? toReplyPreview(replyingTo) : null
    setIsAttachOpen(false)
    try {
      const permission = await Location.requestForegroundPermissionsAsync()
      if (!permission.granted) {
        toast.error(t('messages.location_permission_denied'))
        return
      }
      const position = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      })
      await sendMessage({
        messageType: 'location',
        locationLat: position.coords.latitude,
        locationLng: position.coords.longitude,
        replyToId: replyTo?.id ?? null,
        replyTo,
      })
      setReplyingTo(null)
      productTelemetryService.recordEvent('chat', 'chat.location_send_succeeded', {
        elementId: 'conversation.attach-location',
        elementType: 'button',
      })
      settleToBottom()
    } catch (err) {
      if (__DEV__) console.error('[Chat] location failed:', err)
      toast.error(errorMessage(err, t('messages.location_unavailable')))
      captureError(err, { screen: 'messages', action: 'share_location' }, { conversationId })
    }
  }, [conversationId, replyingTo, sendMessage, settleToBottom, t])

  /**
   * Retry a failed send from its own bubble. Identity-stable for the same
   * reason as `renderItem`: it is a prop of every memoized row.
   */
  const handleRetryMessage = useCallback(
    (message: MessageItem) => {
      productTelemetryService.recordEvent('chat', 'chat.message_send_retry', {
        elementId: 'conversation.retry-send',
        elementType: 'button',
        metadata: { messageType: message.messageType },
      })
      void retryMessage(message).then((ok) => {
        if (ok) {
          settleToBottom()
        } else {
          toast.error(t('messages.send_failed'))
        }
      })
    },
    [retryMessage, settleToBottom, t],
  )

  /**
   * The offline outbox, in the simplest honest form: failed rows ARE the queue.
   *
   * When the connection comes back, every failed send in this thread is
   * re-sent once, automatically. A message that failed in a tunnel should not
   * wait for the user to notice it and tap. Failures that happen while ONLINE
   * (a server rejection) are deliberately NOT auto-retried — those need a human
   * decision, and the bubble offers it.
   */
  const isOnline = useUIStore((state) => state.isOnline)
  const failedMessagesRef = useRef<MessageItem[]>([])
  useEffect(() => {
    failedMessagesRef.current = messagesAsc.filter((message) => message.sendFailed === true)
  }, [messagesAsc])
  const wasOfflineRef = useRef(!isOnline)
  useEffect(() => {
    const cameBackOnline = isOnline && wasOfflineRef.current
    wasOfflineRef.current = !isOnline
    if (!cameBackOnline) return
    const pending = failedMessagesRef.current
    if (pending.length === 0) return
    void Promise.all(pending.map((message) => retryMessage(message)))
  }, [isOnline, retryMessage])

  // `colors` intentionally NOT in the dep array — it changes identity on
  // every theme-toggle render, which would invalidate the memo'd
  // MessageBubble for every row. The bubble reads colors via useTheme()
  // internally; we keep this callback's prop surface to stable primitives.
  const renderItem = useCallback(
    ({ item, index }: { item: MessageItem; index: number }) => {
      const previousMessage = messagesAsc[index - 1]
      const nextMessage = messagesAsc[index + 1]
      const isGroupedWithPrevious = isSameMessageCluster(previousMessage, item)
      const isGroupedWithNext = isSameMessageCluster(item, nextMessage)
      const dayLabel =
        !previousMessage || !isSameDay(previousMessage.createdAt, item.createdAt)
          ? formatDayLabel(item.createdAt, t, i18n.language)
          : null

      return (
        <MessageBubble
          message={item}
          isMine={item.senderId === userId}
          isGroupedWithPrevious={isGroupedWithPrevious}
          isGroupedWithNext={isGroupedWithNext}
          isPending={item.id.startsWith('optimistic-')}
          dayLabel={dayLabel}
          onPressImage={handleOpenImage}
          onRetryMessage={handleRetryMessage}
          onReplyMessage={handleReplyMessage}
          replyAuthorLabel={replyAuthorLabel(item.replyTo)}
          // Cursor comparison, not per-message state: the counterpart's
          // `last_read_at` is a point in time, and every own message at or
          // before it is read. ISO-8601 strings sort lexicographically in the
          // same order as the instants they encode.
          isRead={theirLastReadAt !== null && item.createdAt <= theirLastReadAt}
        />
      )
    },
    [
      handleOpenImage,
      handleRetryMessage,
      handleReplyMessage,
      replyAuthorLabel,
      messagesAsc,
      t,
      i18n.language,
      userId,
      theirLastReadAt,
    ],
  )

  const closeCallSheet = useCallback(() => {
    callPhoneRef.current = null
    setCallSheet(null)
  }, [])

  const handleOpenCall = useCallback(async () => {
    if (!conversationId || callSheet === 'loading') return
    if (Platform.OS !== 'web') impact(ImpactStyle.Light)
    Keyboard.dismiss()
    setCallSheet('loading')
    try {
      const phone = await messagingService.conversationCallPhone(conversationId)
      const href = conversationCallHref(phone)
      if (!href || !phone) {
        callPhoneRef.current = null
        setCallSheet('unavailable')
        return
      }
      callPhoneRef.current = phone
      setCallSheet('ready')
    } catch (err) {
      callPhoneRef.current = null
      setCallSheet(null)
      toast.error(errorMessage(err, t('messages.call_failed')))
      captureError(err, { screen: 'messages', action: 'call_lookup' }, { conversationId })
    }
  }, [callSheet, conversationId, t])

  const handlePlaceCall = useCallback(async () => {
    const href = conversationCallHref(callPhoneRef.current)
    closeCallSheet()
    if (!href) {
      toast.error(t('messages.call_failed'))
      return
    }
    try {
      await Linking.openURL(href)
      productTelemetryService.recordEvent('chat', 'chat.call_placed', {
        elementId: 'conversation.call-button',
        elementType: 'button',
      })
    } catch (err) {
      toast.error(t('messages.call_failed'))
      captureError(err, { screen: 'messages', action: 'call_place' }, { conversationId })
    }
  }, [closeCallSheet, conversationId, t])
  const typingLabel = isOtherTyping
    ? t('messages.typing', {
        name: typingUserName ?? headerTitle,
      })
    : null

  return (
    <ScreenContainer variant={isDesktop ? 'content' : 'full-bleed'} scroll={false} card={false}>
      {/* Header */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: CHAT_HEADER_GEOMETRY.horizontalPadding,
          paddingVertical: CHAT_HEADER_GEOMETRY.verticalPadding,
          borderBottomWidth: 1,
          borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,21,37,0.06)',
          backgroundColor: colors.background,
          gap: CHAT_HEADER_GEOMETRY.gap,
        }}
      >
        <BackButton
          fallbackHref="/(tabs)/messages"
          preferFallback
          size={CHAT_HEADER_GEOMETRY.controlSize}
        />
        {headerTargetId ? (
          <>
            <View
              style={{
                borderWidth: CHAT_HEADER_GEOMETRY.avatarFrameBorderWidth,
                borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(26,21,37,0.08)',
                borderRadius:
                  (CHAT_HEADER_GEOMETRY.avatarSize +
                    CHAT_HEADER_GEOMETRY.avatarFramePadding * 2 +
                    CHAT_HEADER_GEOMETRY.avatarFrameBorderWidth * 2) /
                  2,
                padding: CHAT_HEADER_GEOMETRY.avatarFramePadding,
                backgroundColor: colors.surface,
              }}
            >
              <Avatar
                size="sm"
                source={headerAvatarUrl}
                fallback={headerTitle}
                colorSeed={headerTargetId}
                accessibilityLabel={headerTitle}
              />
            </View>
            <View style={{ flex: 1, justifyContent: 'center', minWidth: 0 }}>
              <Text
                style={{
                  fontSize: CHAT_HEADER_GEOMETRY.titleFontSize,
                  lineHeight: CHAT_HEADER_GEOMETRY.titleLineHeight,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                }}
                numberOfLines={1}
              >
                {headerTitle}
              </Text>
              {typingLabel ? (
                <Text
                  style={{
                    marginTop: 1,
                    fontSize: CHAT_HEADER_GEOMETRY.subtitleFontSize,
                    lineHeight: CHAT_HEADER_GEOMETRY.subtitleLineHeight,
                    fontFamily: FONTS.body,
                    color: colors.textAccent,
                  }}
                  numberOfLines={1}
                >
                  {typingLabel}
                </Text>
              ) : null}
            </View>
            <Pressable
              onPress={handleOpenSearch}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel={t('messages.search_in_conversation')}
              testID="conversation.search-button"
              style={({ pressed }) => ({
                width: CHAT_HEADER_GEOMETRY.controlSize,
                height: CHAT_HEADER_GEOMETRY.controlSize,
                borderRadius: CHAT_HEADER_GEOMETRY.controlSize / 2,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(26,21,37,0.08)',
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Search size={CHAT_COMPOSER_GEOMETRY.iconSize} color={colors.textSecondary} />
            </Pressable>
            <Pressable
              onPress={() => void handleOpenCall()}
              disabled={callSheet === 'loading'}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel={t('messages.call_a11y', { name: headerTitle })}
              testID="conversation.call-button"
              style={({ pressed }) => ({
                width: CHAT_HEADER_GEOMETRY.controlSize,
                height: CHAT_HEADER_GEOMETRY.controlSize,
                borderRadius: CHAT_HEADER_GEOMETRY.controlSize / 2,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: COLORS.coral.DEFAULT,
                opacity: pressed || callSheet === 'loading' ? 0.72 : 1,
              })}
            >
              {callSheet === 'loading' ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Phone size={CHAT_COMPOSER_GEOMETRY.iconSize} color="#FFFFFF" />
              )}
            </Pressable>
            <BlockUserButton
              targetId={headerTargetId}
              targetName={headerTitle}
              onBlocked={() => router.back()}
            >
              {(open) => (
                <Pressable
                  onPress={open}
                  hitSlop={10}
                  accessibilityRole="button"
                  accessibilityLabel={t('block.action')}
                  testID="conversation.block-button"
                  style={({ pressed }) => ({
                    width: CHAT_HEADER_GEOMETRY.controlSize,
                    height: CHAT_HEADER_GEOMETRY.controlSize,
                    borderRadius: CHAT_HEADER_GEOMETRY.controlSize / 2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(26,21,37,0.08)',
                    opacity: pressed ? 0.6 : 1,
                  })}
                >
                  <MoreVertical
                    size={CHAT_COMPOSER_GEOMETRY.iconSize}
                    color={colors.textSecondary}
                  />
                </Pressable>
              )}
            </BlockUserButton>
          </>
        ) : isLoadingConversations ? (
          <>
            <Skeleton
              width={CHAT_HEADER_GEOMETRY.controlSize}
              height={CHAT_HEADER_GEOMETRY.controlSize}
              borderRadius={CHAT_HEADER_GEOMETRY.controlSize / 2}
            />
            <View style={{ flex: 1, gap: CHAT_HEADER_GEOMETRY.avatarFramePadding * 3 }}>
              <Skeleton width="48%" height={15} borderRadius={RADIUS.xs} />
              <Skeleton width="28%" height={11} borderRadius={RADIUS.xs} />
            </View>
          </>
        ) : (
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontSize: CHAT_HEADER_GEOMETRY.titleFontSize,
                lineHeight: CHAT_HEADER_GEOMETRY.titleLineHeight,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
              numberOfLines={1}
            >
              {headerTitle}
            </Text>
          </View>
        )}
      </View>

      {isSearchOpen ? (
        <View
          testID="conversation.search-bar"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.sm,
            paddingHorizontal: CHAT_COMPOSER_GEOMETRY.horizontalPadding,
            paddingVertical: SPACING.sm,
            borderBottomWidth: 1,
            borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,21,37,0.06)',
            backgroundColor: colors.background,
          }}
        >
          <View
            style={{
              flex: 1,
              minHeight: 44,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.xs,
              paddingHorizontal: SPACING.sm,
              borderRadius: RADIUS.full,
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(26,21,37,0.08)',
            }}
          >
            <Search size={18} color={colors.textSecondary} strokeWidth={2.1} />
            <RNTextInput
              ref={searchInputRef}
              value={searchText}
              onChangeText={setSearchText}
              placeholder={t('messages.search_messages_placeholder')}
              placeholderTextColor={colors.textMuted}
              autoFocus={false}
              autoCorrect={false}
              autoCapitalize="none"
              returnKeyType="search"
              style={{
                flex: 1,
                paddingVertical: 0,
                fontFamily: FONTS.body,
                fontSize: 15,
                color: colors.textPrimary,
              }}
              accessibilityLabel={t('messages.search_messages_placeholder')}
              testID="conversation.search-input"
            />
          </View>
          <Pressable
            onPress={handleCloseSearch}
            accessibilityRole="button"
            accessibilityLabel={t('messages.close_search')}
            testID="conversation.search-close"
            style={({ pressed }) => ({
              width: CHAT_HEADER_GEOMETRY.controlSize,
              height: CHAT_HEADER_GEOMETRY.controlSize,
              borderRadius: CHAT_HEADER_GEOMETRY.controlSize / 2,
              alignItems: 'center',
              justifyContent: 'center',
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <X size={20} color={colors.textSecondary} strokeWidth={2.2} />
          </Pressable>
        </View>
      ) : null}

      {/*
        EN ANDROID ESTO NO HACE NADA, A PROPÓSITO, y hay que dejarlo así:
        `behavior='height'` acá provoca el parpadeo al enviar que d6617e6
        arregló, y `validate-chat-ux.mjs` lo vigila. Lo intenté y me equivoqué.

        Pero Android SÍ necesita compensar el teclado, porque la app es
        edge-to-edge desde abril (`edgeToEdgeEnabled: true`) y con eso
        `adjustResize` ya no redimensiona la ventana: el teclado se dibuja
        ENCIMA, tapando el compositor justo mientras la persona escribe. La
        biblioteca de terceros que suele resolver esto está prohibida en este
        repo —crasheaba el proceso de Android al redimensionar o recargar— y
        `validate-chat-ux.mjs` vigila que no vuelva ni por dependencia ni por
        import.

        La salida es no redimensionar el árbol entero sino medir el teclado y
        compensar SOLO el compositor, con la altura que el propio evento trae.
        Eso respeta lo que el guarda protege (no apilar un resize de JS encima
        del nativo) y aun así saca la caja de texto de abajo del teclado.
      */}
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        enabled={Platform.OS === 'ios'}
        keyboardVerticalOffset={0}
      >
        {/* Messages */}
        {isSearchOpen ? (
          <View testID="conversation.search-results" style={{ flex: 1 }}>
            {searchText.trim().length < 2 ? (
              <View
                style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: SPACING.xl,
                  gap: SPACING.sm,
                }}
              >
                <Search size={30} color={colors.textMuted} strokeWidth={1.7} />
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 15,
                    lineHeight: 21,
                    textAlign: 'center',
                    color: colors.textSecondary,
                  }}
                >
                  {t('messages.search_minimum')}
                </Text>
              </View>
            ) : messageSearch.isLoading || messageSearch.isFetching ? (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator size="small" color={COLORS.coral.DEFAULT} />
              </View>
            ) : messageSearch.error ? (
              <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
                <EmptyState.OfflineError onRetry={() => void messageSearch.refetch()} />
              </View>
            ) : messageSearch.results.length === 0 ? (
              <EmptyState
                variant="bloom"
                illustration={<Search size={36} color={colors.textMuted} strokeWidth={1.6} />}
                title={t('messages.search_no_results_title')}
                description={t('messages.search_no_results_desc')}
              />
            ) : (
              <FlashList<MessageSearchResult>
                data={messageSearch.results}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <ConversationSearchResultRow
                    result={item}
                    isMine={item.senderId === userId}
                    otherUserName={headerTitle}
                    locale={i18n.language}
                  />
                )}
                contentContainerStyle={{ paddingTop: SPACING.md, paddingBottom: SPACING.lg }}
                keyboardShouldPersistTaps="handled"
              />
            )}
          </View>
        ) : isLoading ? (
          <View style={{ flex: 1, padding: SPACING.lg, gap: SPACING.md }}>
            {[0, 1, 2].map((i) => (
              <View key={i} style={{ alignItems: i % 2 === 0 ? 'flex-start' : 'flex-end' }}>
                <Skeleton width="60%" height={44} borderRadius={RADIUS.lg} />
              </View>
            ))}
          </View>
        ) : error && messagesAsc.length === 0 ? (
          <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
            <EmptyState.OfflineError onRetry={() => void refetch()} />
          </View>
        ) : messagesAsc.length === 0 ? (
          <View style={{ flex: 1, justifyContent: 'center', paddingHorizontal: SPACING.lg }}>
            {/* Same shell, icon and rhythm as EmptyState.NoMessages on the
                inbox one screen back — the two chat empty states are the same
                moment and used to render as two different components. */}
            <EmptyState
              variant="bloom"
              illustration={
                <MessageCircle size={36} color={COLORS.amber.DEFAULT} strokeWidth={1.6} />
              }
              title={t('messages.empty_conversation_title')}
              description={t('messages.empty_conversation_desc')}
            />
          </View>
        ) : (
          // - data is chronological (messagesAsc) so newest sits at the
          //   end of the array = visual bottom of the screen
          // - maintainVisibleContentPosition.startRenderingFromBottom
          //   replaces FlatList's `inverted` initial-position behavior
          // - autoscrollToBottomThreshold: 0.2 keeps the user pinned to
          //   the latest message ONLY when they're within 20% of the
          //   bottom — if they've scrolled up to read older messages,
          //   incoming messages don't yank them away from what they're
          //   reading
          // - onStartReached fires when the user scrolls UP to the top
          //   (where older messages live) — that's the v2 equivalent
          //   of FlatList-inverted's onEndReached for pagination
          // - ListHeaderComponent shows the loading skeleton at the
          //   visual top where new pages prepend (vs FlatList-inverted
          //   which had it as ListFooterComponent)
          <FlashList<MessageItem>
            ref={listRef}
            testID="conversation.message-list"
            data={messagesAsc}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            maintainVisibleContentPosition={{
              autoscrollToBottomThreshold: 0.2,
              startRenderingFromBottom: true,
            }}
            contentContainerStyle={{ paddingVertical: SPACING.sm }}
            onStartReached={() => {
              if (hasNextPage) fetchNextPage()
            }}
            onStartReachedThreshold={0.3}
            onScroll={handleTrackedListScroll}
            scrollEventThrottle={64}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="interactive"
            ListHeaderComponent={
              <View>
                {tripQuery.data ? (
                  <ConversationTripContext
                    booking={tripQuery.data}
                    onPress={() => router.push(`/trips/${tripQuery.data.id}` as never)}
                  />
                ) : null}
                {isFetchingNextPage ? (
                  <View style={{ alignSelf: 'center', marginVertical: SPACING.md }}>
                    <Skeleton width={160} height={32} borderRadius={RADIUS.lg} />
                  </View>
                ) : null}
              </View>
            }
          />
        )}

        {!isSearchOpen && showJumpToLatest && !isLoading && messagesAsc.length > 0 && (
          <Pressable
            onPress={scrollToBottom}
            accessibilityRole="button"
            accessibilityLabel={t('messages.jump_to_latest')}
            testID="conversation.jump-to-latest"
            style={({ pressed }) => ({
              position: 'absolute',
              right: CHAT_HEADER_GEOMETRY.horizontalPadding,
              // No typing-indicator term: the indicator is header-only now, so
              // nothing between the list and the composer changes height when
              // the other party types.
              bottom:
                composerHeight +
                composerBottomPadding +
                CHAT_COMPOSER_GEOMETRY.topPadding +
                CHAT_COMPOSER_GEOMETRY.outerGap,
              width: CHAT_HEADER_GEOMETRY.controlSize,
              height: CHAT_HEADER_GEOMETRY.controlSize,
              borderRadius: CHAT_HEADER_GEOMETRY.controlSize / 2,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(26,21,37,0.08)',
              opacity: pressed ? 0.78 : 1,
              transform: [{ scale: pressed ? 0.96 : 1 }],
              ...SHADOWS.md,
            })}
          >
            <ChevronDown size={20} color={colors.textPrimary} strokeWidth={2.3} />
          </Pressable>
        )}

        {/* The typing indicator lives in the header subtitle only. A second
            copy sat here, between the list and the composer, and pushed the
            whole message list up and down every time the other party started
            or stopped typing. It also carried the app's only `fontStyle:
            'italic'`, which Montserrat has no face for. */}

        {/*
          BLOQUEADO NO ES DESACTIVADO.
          
          Antes acá vivía el compositor siempre, y la persona escribía un
          mensaje completo para que el servidor se lo rechazara con "No tenés
          permisos para esta acción". Un control que acepta tu trabajo y
          después lo tira es peor que un control que no está.

          Y desactivarlo tampoco alcanza: una caja de texto gris no dice por
          qué, y quien la ve concluye que la app está rota. Lo que va acá es el
          ESTADO, dicho como lo que es —una protección, no un castigo— y con el
          mismo alto y el mismo borde que el compositor, para que al desbloquear
          no salte nada de lugar.
        */}
        {!isSearchOpen &&
          (messagingLocked ? (
            <View
              testID="conversation.locked-notice"
              accessibilityRole="summary"
              accessibilityLabel={`${t('messages.locked_title')}. ${t('messages.locked_body')}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: CHAT_COMPOSER_GEOMETRY.outerGap,
                paddingHorizontal: CHAT_COMPOSER_GEOMETRY.horizontalPadding,
                paddingTop: CHAT_COMPOSER_GEOMETRY.topPadding,
                paddingBottom: composerBottomPadding,
                borderTopWidth: 1,
                borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,21,37,0.06)',
                backgroundColor: colors.background,
              }}
            >
              <View
                style={{
                  width: CHAT_COMPOSER_GEOMETRY.sendButtonSize,
                  height: CHAT_COMPOSER_GEOMETRY.sendButtonSize,
                  borderRadius: CHAT_COMPOSER_GEOMETRY.sendButtonSize / 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.surfaceSubtle,
                }}
              >
                <Lock size={18} color={colors.textSecondary} strokeWidth={2} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 15,
                    lineHeight: 20,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {t('messages.locked_title')}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontSize: 13,
                    lineHeight: 18,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('messages.locked_body')}
                </Text>
              </View>
            </View>
          ) : (
            <View
              style={{
                paddingBottom: composerBottomPadding,
                borderTopWidth: 1,
                borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,21,37,0.06)',
                backgroundColor: colors.background,
              }}
            >
              {replyingTo ? (
                <View
                  testID="conversation.reply-composer-context"
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.xs,
                    paddingHorizontal: CHAT_COMPOSER_GEOMETRY.horizontalPadding,
                    paddingTop: CHAT_COMPOSER_GEOMETRY.topPadding,
                  }}
                >
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <ReplyContext
                      reply={toReplyPreview(replyingTo)}
                      authorLabel={replyAuthorLabel(toReplyPreview(replyingTo))}
                      tone="composer"
                      testID="conversation.reply-context"
                    />
                  </View>
                  <Pressable
                    onPress={() => setReplyingTo(null)}
                    accessibilityRole="button"
                    accessibilityLabel={t('messages.reply_cancel')}
                    testID="conversation.reply-cancel"
                    style={({ pressed }) => ({
                      width: CHAT_COMPOSER_GEOMETRY.iconButtonSize,
                      height: CHAT_COMPOSER_GEOMETRY.iconButtonSize,
                      borderRadius: CHAT_COMPOSER_GEOMETRY.iconButtonSize / 2,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <X size={18} color={colors.textSecondary} strokeWidth={2.2} />
                  </Pressable>
                </View>
              ) : null}
              {/* Quick replies — the Uber move. The four things a rental pair
                actually says are one tap away, so nobody has to type a status
                and nobody needs another app to send one. Hidden while the
                composer has text so the row never competes for attention. */}
              {text.trim().length === 0 ? (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  keyboardShouldPersistTaps="handled"
                  contentContainerStyle={{
                    paddingHorizontal: CHAT_COMPOSER_GEOMETRY.horizontalPadding,
                    paddingTop: CHAT_COMPOSER_GEOMETRY.topPadding,
                    gap: SPACING.sm,
                  }}
                  testID="conversation.quick-replies"
                >
                  {QUICK_REPLIES.map((reply) => (
                    <Pressable
                      key={reply.key}
                      onPress={() => void handleQuickReply(reply.key)}
                      accessibilityRole="button"
                      accessibilityLabel={t(reply.labelKey)}
                      testID={`conversation.quick-reply.${reply.key}`}
                      style={({ pressed }) => ({
                        paddingHorizontal: SPACING.md,
                        minHeight: 36,
                        justifyContent: 'center',
                        borderRadius: RADIUS.full,
                        borderWidth: 1,
                        borderColor: colors.border,
                        backgroundColor: colors.surface,
                        opacity: pressed ? 0.72 : 1,
                      })}
                    >
                      <Text
                        style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textPrimary }}
                      >
                        {t(reply.labelKey)}
                      </Text>
                    </Pressable>
                  ))}
                </ScrollView>
              ) : null}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: CHAT_COMPOSER_GEOMETRY.outerGap,
                  paddingHorizontal: CHAT_COMPOSER_GEOMETRY.horizontalPadding,
                  paddingTop: CHAT_COMPOSER_GEOMETRY.topPadding,
                }}
              >
                <View
                  testID="conversation.input"
                  style={{
                    flex: 1,
                    minHeight: CHAT_COMPOSER_GEOMETRY.inputHeight,
                    height: composerHeight,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: CHAT_COMPOSER_GEOMETRY.innerGap,
                    borderRadius: CHAT_COMPOSER_GEOMETRY.inputHeight / 2,
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(26,21,37,0.08)',
                    paddingLeft: CHAT_COMPOSER_GEOMETRY.innerGap,
                    paddingRight: CHAT_COMPOSER_GEOMETRY.outerGap,
                  }}
                >
                  <Pressable
                    onPress={handleOpenAttach}
                    disabled={isUploading}
                    focusable={false}
                    accessibilityRole="button"
                    accessibilityLabel={
                      isUploading ? t('messages.sending_image') : t('messages.attach_title')
                    }
                    testID="conversation.attach-image"
                    style={({ pressed }) => ({
                      width: CHAT_COMPOSER_GEOMETRY.iconButtonSize,
                      height: CHAT_COMPOSER_GEOMETRY.iconButtonSize,
                      borderRadius: CHAT_COMPOSER_GEOMETRY.iconButtonSize / 2,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: pressed || isUploading ? 0.72 : 1,
                    })}
                  >
                    {isUploading ? (
                      <ActivityIndicator size="small" color={COLORS.coral.DEFAULT} />
                    ) : (
                      <Paperclip
                        size={CHAT_COMPOSER_GEOMETRY.iconSize}
                        color={colors.textSecondary}
                        strokeWidth={2}
                      />
                    )}
                  </Pressable>
                  <RNTextInput
                    ref={inputRef}
                    value={text}
                    onChangeText={handleTextChange}
                    placeholder={t('messages.type_message')}
                    placeholderTextColor={colors.textMuted}
                    multiline
                    maxLength={2000}
                    autoFocus={false}
                    blurOnSubmit={false}
                    returnKeyType="send"
                    enablesReturnKeyAutomatically
                    submitBehavior="submit"
                    onFocus={() => {
                      isInputFocusedRef.current = true
                      settleToBottom()
                    }}
                    onBlur={() => {
                      isInputFocusedRef.current = false
                      setTyping(false)
                    }}
                    onContentSizeChange={(event) => {
                      handleComposerSizeChange(event.nativeEvent.contentSize.height)
                    }}
                    onKeyPress={handleComposerKeyPress}
                    onSubmitEditing={() => {
                      void handleSend('submit_editing')
                    }}
                    style={[
                      {
                        flex: 1,
                        height: composerHeight,
                        maxHeight: CHAT_COMPOSER_GEOMETRY.maxInputHeight,
                        backgroundColor: 'transparent',
                        paddingHorizontal: 0,
                        paddingTop: CHAT_COMPOSER_GEOMETRY.textPaddingVertical,
                        paddingBottom: CHAT_COMPOSER_GEOMETRY.textPaddingVertical,
                        fontSize: CHAT_COMPOSER_GEOMETRY.textFontSize,
                        lineHeight: CHAT_COMPOSER_GEOMETRY.lineHeight,
                        fontFamily: FONTS.body,
                        color: colors.textPrimary,
                        textAlignVertical: composerHeight > COMPOSER_MIN_HEIGHT ? 'top' : 'center',
                      },
                      Platform.OS === 'web'
                        ? ({ outlineStyle: 'none' } as Record<string, string>)
                        : null,
                    ]}
                    // A placeholder is not an accessible name — it disappears on the
                    // first keystroke, and the audit read this composer as unlabelled.
                    accessibilityLabel={t('messages.type_message')}
                    testID="conversation.message-input"
                  />
                </View>
                <Pressable
                  onPress={() => void handleSend('button')}
                  disabled={!text.trim() || isSending}
                  focusable={false}
                  accessibilityRole="button"
                  accessibilityLabel={t('messages.send')}
                  testID="conversation.send-button"
                  style={({ pressed }) => ({
                    width: CHAT_COMPOSER_GEOMETRY.sendButtonSize,
                    height: CHAT_COMPOSER_GEOMETRY.sendButtonSize,
                    borderRadius: CHAT_COMPOSER_GEOMETRY.sendButtonSize / 2,
                    // `handleSend` clears the composer before awaiting, so `text` is
                    // already empty while the send is in flight. Without `isSending`
                    // here the button greys out mid-send and the white spinner lands
                    // on a near-white surface.
                    backgroundColor:
                      text.trim() || isSending ? COLORS.coral.DEFAULT : colors.surfaceSubtle,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: text.trim() || isSending ? 0 : 1,
                    borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(26,21,37,0.07)',
                    opacity: pressed || isSending ? 0.82 : 1,
                    transform: [{ scale: pressed ? 0.96 : 1 }],
                  })}
                >
                  {isSending ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Send
                      size={CHAT_COMPOSER_GEOMETRY.sendIconSize}
                      color={text.trim() ? '#FFFFFF' : colors.textMuted}
                      strokeWidth={2}
                    />
                  )}
                </Pressable>
              </View>
            </View>
          ))}
      </KeyboardAvoidingView>

      {/* Attach sheet. One place for the three ways into a message that are not
          typing: camera, gallery, location. Camera and the location pin are
          exactly the two things a rental pair used to leave for WhatsApp to
          exchange — both now land in the thread as first-class messages. */}
      <AppModal
        visible={isAttachOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsAttachOpen(false)}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          onPress={() => setIsAttachOpen(false)}
          style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(12,8,24,0.42)' }}
        >
          <View
            style={{
              backgroundColor: colors.surface,
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
              paddingTop: SPACING.md,
              paddingBottom: insets.bottom + SPACING.md,
              paddingHorizontal: SPACING.lg,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 16,
                color: colors.textPrimary,
                marginBottom: SPACING.xs,
              }}
            >
              {t('messages.attach_title')}
            </Text>
            <AttachOption
              icon={<Camera size={22} color={colors.textPrimary} strokeWidth={2} />}
              label={t('messages.attach_camera')}
              testID="conversation.attach-camera"
              onPress={() => void handleTakePhoto()}
            />
            <AttachOption
              icon={<ImageIcon size={22} color={colors.textPrimary} strokeWidth={2} />}
              label={t('messages.attach_gallery')}
              testID="conversation.attach-gallery"
              onPress={() => void handlePickImage()}
            />
            <AttachOption
              icon={<MapPin size={22} color={colors.textPrimary} strokeWidth={2} />}
              label={t('messages.attach_location')}
              testID="conversation.attach-location"
              onPress={() => void handleShareLocation()}
            />
          </View>
        </Pressable>
      </AppModal>

      <AppModal
        visible={callSheet === 'ready' || callSheet === 'unavailable'}
        transparent
        animationType="fade"
        onRequestClose={closeCallSheet}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          onPress={closeCallSheet}
          style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(12,8,24,0.42)' }}
        >
          <Pressable
            onPress={() => undefined}
            style={{
              backgroundColor: colors.surface,
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
              paddingTop: SPACING.lg,
              paddingBottom: insets.bottom + SPACING.lg,
              paddingHorizontal: SPACING.lg,
              gap: SPACING.md,
            }}
          >
            <View
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 90, 95, 0.12)',
              }}
            >
              <Phone size={22} color={COLORS.coral.DEFAULT} />
            </View>
            <View style={{ gap: SPACING.xs }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 20,
                  color: colors.textPrimary,
                }}
              >
                {callSheet === 'unavailable'
                  ? t('messages.call_unavailable_title')
                  : t('messages.call_title', { name: headerTitle })}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  color: colors.textSecondary,
                }}
              >
                {callSheet === 'unavailable'
                  ? t('messages.call_unavailable_body', { name: headerTitle })
                  : t('messages.call_body')}
              </Text>
            </View>
            {callSheet === 'ready' ? (
              <Button
                title={t('messages.call_action')}
                onPress={() => void handlePlaceCall()}
                testID="conversation.call-confirm"
              />
            ) : null}
            <Button
              title={t('messages.call_cancel')}
              variant="text"
              onPress={closeCallSheet}
              testID="conversation.call-cancel"
            />
          </Pressable>
        </Pressable>
      </AppModal>

      <PhotoLightbox
        visible={lightbox.visible}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        onClose={lightbox.close}
        testIDPrefix="chat-photo-lightbox"
      />
    </ScreenContainer>
  )
}
